import React, { useState, useRef, useEffect } from 'react';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter description...',
  descriptionType = 'text',
  descriptionItems = [],
  onTypeChange,
  onItemsChange,
  className = ''
}) => {
  // Safely ensure descriptionItems is an array
  const safeDescriptionItems = Array.isArray(descriptionItems) ? descriptionItems : [];
  
  const [isListMode, setIsListMode] = useState(descriptionType === 'list');
  const [isMixedMode, setIsMixedMode] = useState(descriptionType === 'mixed');
  const [items, setItems] = useState(safeDescriptionItems.length > 0 ? safeDescriptionItems : ['']);
  const [introText, setIntroText] = useState(value || '');
  const editorRef = useRef(null);

  useEffect(() => {
    // Safely ensure descriptionItems is an array for processing
    const itemsToUse = Array.isArray(descriptionItems) ? descriptionItems : [];
    
    if (descriptionType === 'list' && itemsToUse.length > 0) {
      setItems(itemsToUse);
    } else if (descriptionType === 'text') {
      setItems(['']);
    } else if (descriptionType === 'mixed') {
      setItems(itemsToUse.length > 0 ? itemsToUse : ['']);
      setIntroText(value || '');
    }
  }, [descriptionType, descriptionItems, value]);

  const handleTextChange = (e) => {
    onChange(e.target.value);
  };

  const handleTypeChange = (newType) => {
    setIsListMode(newType === 'list');
    setIsMixedMode(newType === 'mixed');
    onTypeChange?.(newType);
    
    if (newType === 'text') {
      // Convert list items to text
      const textValue = items.filter(item => item.trim()).join(' ');
      onChange(textValue);
      onItemsChange?.([]);
    } else if (newType === 'list') {
      // Convert text to list
      const textItems = value ? value.split('.').map(item => item.trim()).filter(item => item) : [''];
      setItems(textItems.length > 0 ? textItems : ['']);
      onItemsChange?.(textItems.length > 0 ? textItems : ['']);
    } else if (newType === 'mixed') {
      // Keep current text as intro and items as list
      setIntroText(value || '');
      if (items.length === 0) {
        setItems(['']);
        onItemsChange?.(['']);
      }
    }
  };

  const handleIntroTextChange = (e) => {
    const newText = e.target.value;
    setIntroText(newText);
    onChange(newText);
    console.log('🔍 RichTextEditor - Intro text changed:', newText);
  };

  const handleItemChange = (index, newValue) => {
    const newItems = [...items];
    newItems[index] = newValue;
    setItems(newItems);
    onItemsChange?.(newItems);
    console.log('🔍 RichTextEditor - Item changed:', { index, newValue, allItems: newItems });
  };

  const addItem = () => {
    const newItems = [...items, ''];
    setItems(newItems);
    onItemsChange?.(newItems);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      onItemsChange?.(newItems);
    }
  };

  const moveItemUp = (index) => {
    if (index > 0) {
      const newItems = [...items];
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
      setItems(newItems);
      onItemsChange?.(newItems);
    }
  };

  const moveItemDown = (index) => {
    if (index < items.length - 1) {
      const newItems = [...items];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      setItems(newItems);
      onItemsChange?.(newItems);
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Type Toggle */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-300">Description Type:</label>
        <div className="flex bg-gray-800 rounded-lg p-1">
          <button
            type="button"
            onClick={() => handleTypeChange('text')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              !isListMode && !isMixedMode
                ? 'bg-cyan-600 text-white' 
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Text
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange('list')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              isListMode && !isMixedMode
                ? 'bg-cyan-600 text-white' 
                : 'text-gray-300 hover:text-white'
            }`}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange('mixed')}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              isMixedMode
                ? 'bg-cyan-600 text-white' 
                : 'text-gray-300 hover:text-white'
            }`}
          >
            Text + List
          </button>
        </div>
      </div>

      {/* Editor Content */}
      {isMixedMode ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Introduction Text:</label>
            <textarea
              value={introText}
              onChange={handleIntroTextChange}
              placeholder="Enter introduction text..."
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">List Items:</label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {items.map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-800 rounded-lg p-3">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => moveItemUp(index)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItemDown(index)}
                      disabled={index === items.length - 1}
                      className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="flex-1">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleItemChange(index, e.target.value)}
                      placeholder={`Item ${index + 1}`}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
                    />
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    className="p-2 text-red-400 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Remove item"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            
            <button
              type="button"
              onClick={addItem}
              className="w-full py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg border border-gray-600 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>
        </div>
      ) : isListMode ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">Description Items:</label>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {items.map((item, index) => (
              <div key={index} className="flex items-center gap-2 bg-gray-800 rounded-lg p-3">
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => moveItemUp(index)}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItemDown(index)}
                    disabled={index === items.length - 1}
                    className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                
                <div className="flex-1">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleItemChange(index, e.target.value)}
                    placeholder={`Item ${index + 1}`}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
                  />
                </div>
                
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  disabled={items.length === 1}
                  className="p-2 text-red-400 hover:text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove item"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          
          <button
            type="button"
            onClick={addItem}
            className="w-full py-2 px-4 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg border border-gray-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Item
          </button>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Description:</label>
          <textarea
            ref={editorRef}
            value={value}
            onChange={handleTextChange}
            placeholder={placeholder}
            rows={4}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-white placeholder-gray-400"
          />
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
